/* wtf-teams-validate.js
 *
 * Makes the House Divided personalization fields genuinely required, and makes
 * their values actually reach the cart.
 *
 * Two independent problems are solved here, because either one alone loses the
 * customer's entries silently:
 *
 * 1. CAPTURE. The fields live ~38,000 characters away from the add-to-cart form
 *    and OUTSIDE it. The previous approach associated them with the form via a
 *    JS-set `form=` attribute, which depends on script timing and does not
 *    survive Dawn re-rendering the form on a variant change. Instead of relying
 *    on that, this script copies every value into hidden inputs placed INSIDE
 *    the form immediately before submit, so FormData always picks them up.
 *
 * 2. VALIDATION. The fields carry `required`, but that attribute cannot fire:
 *    Dawn sets `novalidate` on the form and <product-form> submits over fetch(),
 *    so native constraint validation never runs. Validation is done here, in
 *    capture phase, ahead of Dawn's own submit handler.
 *
 * The dynamic checkout button (Buy it now / Shop Pay) bypasses the form entirely
 * and cannot be intercepted, so it is disabled until the fields are valid.
 *
 * 2026-10-09 (order #1332): once the fields were valid, Shop Pay was enabled while
 * the form still held NO team values (the mirror ran only on the Add tap), so a
 * Shop Pay buy on this page could reach checkout without the teams. The mirror now
 * runs on every keystroke and choice, again whenever the theme re-renders the form,
 * and once more on the first touch of the payment button. Add to cart is unchanged.
 *
 * 2026-10-13 (CRO phase 2, UX-AUDIT findings 5e and 8):
 *   - gate() now also writes one visible line next to the dimmed payment button ("Add your teams to use Shop Pay"; "Add your
 *     date to use Shop Pay" when only the Cancer-Free date box is incomplete). A phone never shows the title attribute.
 *     Styles: assets/wtf-size-sheet.css (.wtf-teams__wallet-note).
 *   - The click guard now also matches the live sticky button class .wtf-ff-sticky-atc__btn (the old .wtf-sticky-atc__btn matched
 *     nothing). Before a size is chosen that button opens the size sheet, so the guard steps aside then and the sheet still opens.
 *   - label() drops a trailing "?" so the error sentence reads "Please fill in Team 1, Who loves Team 1, ..." after the label
 *     change to "Who loves Team 1?". The property KEYS (name attributes) are untouched.
 *
 * 2026-10-31 (orders #1870 and #1983, "Team 2 - Who: Other" with no "(Other)" name): active() decided by the box's inline
 *   display, and sync() ran only at init and on the select's change event. When the browser restores form state after a
 *   reload or a back-navigation (common in the Facebook in-app browser) the select comes back as Other and the typed name
 *   comes back in the still-hidden box, with no change event. missing() and mirror() then skipped the box: no warning,
 *   and the cart line had no "(Other)". Now:
 *   - active() reads the sibling select (same parent, as bindOther finds it) and ignores style.display.
 *   - sync() no longer clears the box when it runs at init or on a page show; only a real change away from Other clears it.
 *   - every sync() runs again on pageshow (also when persisted) and on load, then gate() refills the mirror.
 *   - missing() treats an active (Other) box as required even before sync() has set the attribute, so an empty one is flagged.
 */
(function () {
  'use strict';

  var ERR = 'wtf-teams__input--error';
  var MIRROR = 'data-wtf-mirror';

  function boxes() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-wtf-teams]'));
  }

  function cartForm() {
    return (
      document.querySelector('product-form form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]:not([id*="installment"])')
    );
  }

  function fields(box) {
    return Array.prototype.slice
      .call(box.querySelectorAll("[name^='properties[']"))
      .filter(function (el) {
        return !el.hasAttribute(MIRROR);
      });
  }

  /* "(Other)" inputs only count while their select reads Other. The select decides, never the box's display:
     a restored form can set the select with no change event, while the box is still hidden (2026-10-31). */
  function active(el) {
    if (!el.classList.contains('wtf-teams__other')) return true;
    var sel = el.parentNode && el.parentNode.querySelector('select');
    return !!sel && sel.value === 'Other';
  }

  function val(el) {
    return String(el.value == null ? '' : el.value).trim();
  }

  function missing(box) {
    return fields(box).filter(function (el) {
      /* An active (Other) box is always required: sync() may not have run yet after a form restore. */
      var need = el.hasAttribute('required') || el.classList.contains('wtf-teams__other');
      return need && active(el) && val(el) === '';
    });
  }

  function label(el) {
    var l = el.id ? document.querySelector('label[for="' + el.id + '"]') : null;
    if (l && l.textContent.trim()) return l.textContent.trim().replace(/\?$/, '');
    return (el.getAttribute('name') || '').replace(/^properties\[/, '').replace(/\]$/, '');
  }

  function note(box) {
    var id = box.id + '-wtf-error';
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('p');
      el.id = id;
      el.className = 'wtf-teams__error';
      el.setAttribute('role', 'alert');
      el.hidden = true;
      box.appendChild(el);
    }
    return el;
  }

  function clear(box) {
    fields(box).forEach(function (el) {
      el.classList.remove(ERR);
      el.removeAttribute('aria-invalid');
    });
    var n = note(box);
    n.hidden = true;
    n.textContent = '';
  }

  function show(box, bad) {
    bad.forEach(function (el) {
      el.classList.add(ERR);
      el.setAttribute('aria-invalid', 'true');
    });
    var n = note(box);
    var names = bad.map(label);
    n.textContent =
      'Please fill in ' + (names.length === 1 ? names[0] : names.join(', ')) + ' before adding to cart.';
    n.hidden = false;
    try {
      bad[0].focus({ preventScroll: true });
    } catch (e) {
      bad[0].focus();
    }
    clearStickyBar(n, bad[0]);
  }

  /* R543 round 2: the mobile sticky Add bar covered part of the message. Scroll at once (no smooth) so the message ends
     above the bar (or above the screen bottom when the bar is hidden), with 12 px to spare. If there is room left, bring the
     first empty field in view below the header. The message wins when both cannot fit. */
  function clearStickyBar(note, field) {
    var bar = document.querySelector('.wtf-ff-sticky-atc--visible');
    var limit = window.innerHeight;
    if (bar) {
      var bt = bar.getBoundingClientRect().top;
      if (bt > 0 && bt < limit) limit = bt;
    }
    var r = note.getBoundingClientRect();
    var f = field.getBoundingClientRect();
    var down = r.bottom + 12 - limit;
    var up = f.top - 70;
    var s = 0;
    if (down > 0) s = down;
    else if (up < 0) s = Math.max(up, down);
    if (!s) return;
    try {
      window.scrollBy({ top: s, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollBy(0, s);
    }
  }

  function valid(reveal) {
    var ok = true;
    boxes().forEach(function (box) {
      var bad = missing(box);
      if (bad.length) {
        ok = false;
        if (reveal) show(box, bad);
      } else {
        clear(box);
      }
    });
    return ok;
  }

  /* THE FIX FOR CAPTURE: put real inputs inside the form so FormData sees them.
     Rebuilt from scratch on every submit so stale values can never be sent. */
  function mirror() {
    var form = cartForm();
    if (!form) return 0;
    Array.prototype.slice.call(form.querySelectorAll('[' + MIRROR + ']')).forEach(function (n) {
      n.parentNode.removeChild(n);
    });
    var count = 0;
    boxes().forEach(function (box) {
      fields(box).forEach(function (el) {
        if (!active(el)) return;
        var v = val(el);
        if (v === '') return;
        var h = document.createElement('input');
        h.type = 'hidden';
        h.name = el.getAttribute('name');
        h.value = v;
        h.setAttribute(MIRROR, '');
        form.appendChild(h);
        count++;
      });
    });
    return count;
  }

  /* True when the form already holds exactly the current non-empty values. */
  function inSync() {
    var form = cartForm();
    if (!form) return true;
    var want = [];
    boxes().forEach(function (box) {
      fields(box).forEach(function (el) {
        if (active(el) && val(el) !== '') want.push(el.getAttribute('name') + '=' + val(el));
      });
    });
    var have = Array.prototype.slice.call(form.querySelectorAll('[' + MIRROR + ']')).map(function (h) {
      return h.name + '=' + h.value;
    });
    return want.join('|') === have.join('|');
  }

  /* 2026-10-13 (finding 5e): the visible reason the payment button is dimmed. */
  function walletNoteText() {
    var dates = 0;
    var teams = 0;
    boxes().forEach(function (b) {
      if (missing(b).length === 0) return;
      if (b.hasAttribute('data-wtf-cf-date')) dates++;
      else teams++;
    });
    return dates && !teams ? 'Add your date to use Shop Pay' : 'Add your teams to use Shop Pay';
  }

  function walletNote(w, ok) {
    /* A payment-button wrapper can sit inside another match of the same selector. Write the note once, on the outer one. */
    if (w.parentElement && w.parentElement.closest('.shopify-payment-button, [data-shopify="payment-button"]')) return;
    var n = w.nextElementSibling;
    if (!n || !n.classList || !n.classList.contains('wtf-teams__wallet-note')) {
      if (ok || !w.parentNode) return;
      n = document.createElement('p');
      n.className = 'wtf-teams__wallet-note';
      w.parentNode.insertBefore(n, w.nextSibling);
    }
    var text = walletNoteText();
    if (n.textContent !== text) n.textContent = text;
    n.hidden = ok;
  }

  function gate() {
    if (!inSync()) mirror();
    var ok = boxes().every(function (b) {
      return missing(b).length === 0;
    });
    Array.prototype.forEach.call(
      document.querySelectorAll('.shopify-payment-button, [data-shopify="payment-button"]'),
      function (w) {
        w.style.opacity = ok ? '' : '0.45';
        w.style.pointerEvents = ok ? '' : 'none';
        w.setAttribute('aria-disabled', ok ? 'false' : 'true');
        if (ok) w.removeAttribute('title');
        else w.setAttribute('title', 'Fill in your teams above first');
        walletNote(w, ok);
      }
    );
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  /* Runs ahead of Dawn's handler. Blocks when incomplete, mirrors when complete. */
  function guard(e) {
    if (!boxes().length) return;
    if (!valid(true)) {
      stop(e);
      return;
    }
    mirror();
  }

  var syncs = [];

  function bindOther(box) {
    Array.prototype.forEach.call(box.querySelectorAll('select'), function (sel) {
      var other = sel.parentNode.querySelector('.wtf-teams__other');
      if (!other) return;
      /* clearIt is true only for a real change away from Other. At init and on a page show the box keeps a restored value. */
      function sync(clearIt) {
        if (sel.value === 'Other') {
          other.style.display = 'block';
          other.setAttribute('required', 'required');
        } else {
          other.style.display = 'none';
          other.removeAttribute('required');
          if (clearIt === true) other.value = '';
        }
        gate();
      }
      sel.addEventListener('change', function () {
        sync(true);
      });
      syncs.push(sync);
      sync();
    });
  }

  /* A form restored by the browser (reload, back-navigation) sets the selects after init with no change event. */
  function resyncAll() {
    syncs.forEach(function (s) {
      s();
    });
    gate();
  }

  function init() {
    var all = boxes();
    if (!all.length) return;

    all.forEach(bindOther);

    /* Capture phase on the document catches the submit no matter which form
       instance Dawn has re-rendered, so this survives variant changes. */
    document.addEventListener(
      'submit',
      function (e) {
        if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) guard(e);
      },
      true
    );

    /* Covers the main button and the sticky bar, which act on click. */
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        if (
          !e.target.closest(
            'button[name="add"], .product-form__submit, .wtf-sticky-atc__btn, .wtf-ff-sticky-atc__btn, [data-wtf-sticky-atc]'
          )
        )
          return;
        /* 2026-10-13 (finding 8): before a size is chosen the sticky button opens the size sheet (wtf-ff-pdp.js). Do not block that tap
           with the teams message; the sheet's Add reaches the real Add button, which this guard does match. */
        if (
          e.target.closest('.wtf-ff-sticky-atc__btn') &&
          window.wtfFfPdp &&
          typeof window.wtfFfPdp.isSizeChosen === 'function' &&
          !window.wtfFfPdp.isSizeChosen()
        )
          return;
        guard(e);
      },
      true
    );

    all.forEach(function (box) {
      fields(box).forEach(function (el) {
        ['input', 'change'].forEach(function (n) {
          el.addEventListener(n, function () {
            if (val(el) !== '') {
              el.classList.remove(ERR);
              el.removeAttribute('aria-invalid');
            }
            if (missing(box).length === 0) clear(box);
            gate();
          });
        });
      });
    });

    /* The theme re-renders the product form on a variant change, which drops the
       mirrored inputs. Put them back. inSync() stops the observer from looping on
       its own writes. */
    var section = (cartForm() && cartForm().closest('[id^="MainProduct"], .product, section')) || document.body;
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        if (!inSync()) mirror();
        /* 2026-10-13: a theme re-render replaces the payment button and drops the dimming and the note. Put them back.
           gate() only writes attributes and, when the note is missing, one element, so it cannot feed this observer in a loop. */
        gate();
      }, 30);
    }).observe(section, { childList: true, subtree: true });

    /* Last chance before Shop Pay / Buy it now reads the form. */
    ['pointerdown', 'touchstart', 'mousedown', 'keydown'].forEach(function (n) {
      document.addEventListener(
        n,
        function (e) {
          if (e.target && e.target.closest && e.target.closest('.shopify-payment-button, [data-shopify="payment-button"]')) mirror();
        },
        true
      );
    });

    window.addEventListener('pageshow', resyncAll);
    window.addEventListener('load', resyncAll);

    gate();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
